import { zodResolver } from '@hookform/resolvers/zod';
import {
  IInvoiceEmailTiming,
  InvoiceEmailTimingSchema,
} from '@monetize/types/app';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  MAccordion,
  MButton,
  MDivider,
  MFlex,
  MFormField,
  MFormFieldProps,
  MGrid,
  MGridItem,
  MInput,
  MRadio,
  MRadioGroup,
  MStack,
  MSwitch,
} from '../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../components/Monetize/MSettingAccordionItem';
import { useAccordionControl } from '../../../../hooks';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../hooks/useSettingsSectionHighlight';
import { useConfirmModal } from '../../../../services/confirmModal';

export type InvoiceEmailTimingProps = {
  emailTiming: IInvoiceEmailTiming;
  onSubmit: (data: IInvoiceEmailTiming) => Promise<void>;
};

const baseFieldProps: MFormFieldProps = {
  orientation: 'horizontal',
  gap: 4,
  labelProps: { m: 0, fontWeight: 'normal', flex: '1' },
};

const fieldProps: MFormFieldProps = {
  ...baseFieldProps,
  borderBottomWidth: '1px',
  paddingBottom: 2,
  borderColor: 'border.subtle',
};

export const InvoiceEmailTiming = ({
  emailTiming,
  onSubmit,
}: InvoiceEmailTimingProps) => {
  const isHighlighted = useSettingsSectionHighlight('invoiceSending');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailInvoiceTimingAuto, setIsEmailInvoiceTimingAuto] =
    useState(false);
  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useEffect(() => {
    if (isHighlighted) {
      setAccordionValue(['0']);
    }
  }, [isHighlighted, setAccordionValue]);
  const { showConfirmModal, setModalLoading, closeModal, modalData } =
    useConfirmModal();

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset,
    getValues,
  } = useForm<IInvoiceEmailTiming>({
    resolver: zodResolver(InvoiceEmailTimingSchema),
    mode: 'onChange',
    defaultValues: {
      automatic: true,
      attachInvoicePdf: false,
      showUnpaidLinksInPDF: false,
      fromName: '',
      replyTo: '',
    },
  });

  useEffect(() => {
    if (emailTiming && !modalData) {
      reset(emailTiming);
    }
  }, [emailTiming, reset, modalData]);

  const handleSubmitEmailTiming = async (data: IInvoiceEmailTiming) => {
    try {
      setIsLoading(true);
      await onSubmit(data);
    } catch (err: any) {
      handleApiErrorToast(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEmailInvoiceTimingAuto) {
      if (isDirty && isValid) {
        const onYes = async () => {
          setModalLoading(true);
          const values = getValues();
          await handleSubmitEmailTiming(values);
          setModalLoading(false);
          setIsEmailInvoiceTimingAuto(false);
          closeModal();
        };

        showConfirmModal({
          title: `Are you sure?`,
          description:
            'MonetizeNow will email your customers automatically when invoices are created.',
          onYes,

          yesBtnProps: {
            variant: 'primary' as any,
          },
          onNo: () => {
            setIsEmailInvoiceTimingAuto(false);
            closeModal();
          },
          noBtnProps: {
            variant: 'cancel' as any,
          },
        });
      }
    }
  }, [
    isEmailInvoiceTimingAuto,
    isDirty,
    isValid,
    showConfirmModal,
    closeModal,
  ]);

  return (
    <MAccordion
      collapsible
      w="100%"
      multiple
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <MSettingAccordionItem
        value="0"
        id="invoiceSending"
        description="Define Invoice sending behavior for newly created Bill Groups."
        label="Invoice Sending"
        {...getHighlightStyles(isHighlighted)}
        borderWidth="1px"
      >
        <form onSubmit={handleSubmit(handleSubmitEmailTiming)}>
          <MGrid templateColumns="repeat(12, 1fr)" gap={2}>
            <MGridItem colSpan={12}>
              <MFormField error={errors.automatic} {...fieldProps}>
                <Controller
                  name="automatic"
                  control={control}
                  render={({ field: { value, onChange, ...rest } }) => (
                    <MRadioGroup
                      onChange={(val) => {
                        setIsEmailInvoiceTimingAuto(val === 'true');
                        onChange && onChange(val === 'true');
                      }}
                      value={
                        typeof value === 'undefined' ? 'false' : String(value)
                      }
                      {...rest}
                      isDisabled={isLoading}
                    >
                      <MStack direction="column">
                        <MRadio value="true">Automatically</MRadio>
                        <MRadio value="false">Manually</MRadio>
                      </MStack>
                    </MRadioGroup>
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem colSpan={12}>
              <MFormField
                label="Attach invoice as PDF to email"
                error={errors.attachInvoicePdf}
                {...fieldProps}
              >
                <Controller
                  name="attachInvoicePdf"
                  control={control}
                  render={({ field: { value, ...rest } }) => (
                    <MSwitch
                      isChecked={value}
                      isDisabled={isLoading}
                      {...rest}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
            <>
              <MGridItem colSpan={12}>
                <MFormField
                  label="Email From Name"
                  tooltip={`The name that will appear as the sender of the email. If not provided, "MonetizeNow Team" name will be used.`}
                  error={errors.fromName}
                  // {...fieldProps}
                >
                  <Controller
                    name="fromName"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput value={value ?? ''} {...rest} />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={12}>
                <MFormField
                  label="Email Reply To"
                  tooltip={`Email address to which replies will be sent. If not provided, "noreply@monetizenow.io" will be used.`}
                  error={errors.replyTo}
                  // {...fieldProps}
                >
                  <Controller
                    name="replyTo"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput value={value ?? ''} {...rest} />
                    )}
                  />
                </MFormField>
              </MGridItem>
            </>
          </MGrid>
          <MDivider my={4} />
          <MFlex justifyContent="flex-end">
            <MButton
              type="submit"
              variant="primary"
              alignSelf="self-end"
              isDisabled={isLoading || !isDirty || !isValid}
              isLoading={isLoading}
              onClick={handleSubmit(handleSubmitEmailTiming)}
            >
              Save
            </MButton>
          </MFlex>
        </form>
      </MSettingAccordionItem>
    </MAccordion>
  );
};
